
import React, { useContext, useState } from 'react';
import { AppContext } from '../contexts/AppContext';
import { useUI } from '../contexts/UIContext';
import { Rol, ReglaPuntos } from '../types';
import Modal from '../components/Modal';

const ReglaForm: React.FC<{ 
    regla: Partial<ReglaPuntos>; 
    onSave: (regla: Partial<ReglaPuntos>) => Promise<void>; 
    onCancel: () => void; 
}> = ({ regla, onSave, onCancel }) => {
    const [formData, setFormData] = useState<Partial<ReglaPuntos>>({
        descripcion: '',
        puntos: 1,
        cada: 100,
        categoria: '',
        ...regla
    });
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = e.target;
        const newValue = type === 'number' ? parseFloat(value) : value;
        setFormData(prev => ({ ...prev, [name]: newValue }));
    };

    const ui = useUI();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if(!formData.descripcion) {
            ui.showModal('Error', 'La descripción es obligatoria');
            return;
        }
        setIsSaving(true);
        await onSave(formData);
        setIsSaving(false);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700">Descripción</label>
                <input 
                    type="text" 
                    name="descripcion" 
                    value={formData.descripcion || ''} 
                    onChange={handleChange} 
                    className="w-full p-2 border rounded mt-1" 
                    required 
                    placeholder="Ej: Puntos por consumo general"
                />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Puntos a otorgar</label>
                    <input 
                        type="number" 
                        name="puntos" 
                        value={formData.puntos || ''} 
                        onChange={handleChange} 
                        className="w-full p-2 border rounded mt-1" 
                        required 
                        min="1"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Cada $ gastados</label>
                    <input 
                        type="number" 
                        name="cada" 
                        value={formData.cada || ''} 
                        onChange={handleChange} 
                        className="w-full p-2 border rounded mt-1" 
                        required 
                        min="1"
                    />
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Categoría (Opcional)</label>
                <input 
                    type="text" 
                    name="categoria" 
                    value={formData.categoria || ''} 
                    onChange={handleChange} 
                    className="w-full p-2 border rounded mt-1" 
                    placeholder="Ej: Bebidas (Dejar vacío para general)"
                />
                <p className="text-xs text-gray-500 mt-1">Si especificás una categoría, la regla solo aplicará a productos de esa categoría.</p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
                <button type="button" onClick={onCancel} className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400">Cancelar</button>
                <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400" disabled={isSaving}>
                    {isSaving ? 'Guardando...' : 'Guardar'}
                </button>
            </div>
        </form>
    );
};

const ConfiguracionScreen: React.FC = () => {
    const { user, reglasPuntos, addReglaPunto, updateReglaPunto, deleteReglaPunto, isReadOnly } = useContext(AppContext);
    const ui = useUI();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRegla, setSelectedRegla] = useState<ReglaPuntos | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [reglaToDelete, setReglaToDelete] = useState<ReglaPuntos | null>(null);

    
    if (user?.rol !== Rol.ADMIN) {
        return <p>Acceso denegado.</p>;
    }

    const handleOpenModal = (regla?: ReglaPuntos) => {
        setSelectedRegla(regla || null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedRegla(null);
    };

    const handleSaveRegla = async (reglaData: Partial<ReglaPuntos>) => {
        try {
            let success = false;
            if (selectedRegla) {
                success = await updateReglaPunto({ ...selectedRegla, ...reglaData } as ReglaPuntos);
            } else {
                success = await addReglaPunto(reglaData as Omit<ReglaPuntos, 'id' | 'club_id'>);
            }
            if (success) handleCloseModal();
        } catch (error: any) {
            ui.showModal('Error', error.message || 'Error inesperado');
        }
    };

    const handleDeleteClick = (regla: ReglaPuntos) => {
        setReglaToDelete(regla);
    };

    const confirmDelete = async () => {
        if (!reglaToDelete) return;
        const id = reglaToDelete.id;
        setDeletingId(id);
        setReglaToDelete(null);

        const result = await deleteReglaPunto(id);
        
        if (!result.success) {
            ui.showModal('Error', result.message || "Error al eliminar la regla.");
        }
        setDeletingId(null);
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Reglas del Sistema de Puntos</h2>
                    <p className="text-gray-600 mt-1">Definí cómo los socios ganan puntos con sus compras.</p>
                </div>
                <button 
                    onClick={() => handleOpenModal()}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 whitespace-nowrap w-full sm:w-auto disabled:bg-gray-400"
                    disabled={isReadOnly}
                >
                    Nueva Regla
                </button>
            </div>
            
            <div className="space-y-4">
                {reglasPuntos.map(regla => (
                    <div key={regla.id} className="border p-4 rounded-lg flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                        <div>
                            <p className="font-bold text-gray-900">{regla.descripcion}</p>
                            <p className="text-sm text-gray-700">
                                Gana <strong>{regla.puntos} punto(s)</strong> por cada <strong>${regla.cada.toLocaleString('es-AR')}</strong> gastados
                                {regla.categoria && ` en la categoría "${regla.categoria}"`}.
                            </p>
                        </div>
                        <div className="flex gap-3 self-start sm:self-center mt-2 sm:mt-0">
                            <button 
                                onClick={() => handleOpenModal(regla)}
                                className="text-yellow-600 hover:underline font-medium disabled:text-gray-400"
                                disabled={isReadOnly}
                            >
                                Editar
                            </button>
                            <button 
                                onClick={() => handleDeleteClick(regla)}
                                className="text-red-600 hover:underline font-medium disabled:text-gray-400"
                                disabled={isReadOnly || deletingId === regla.id}
                            >
                                {deletingId === regla.id ? 'Eliminando...' : 'Eliminar'}
                            </button>
                        </div>
                    </div>
                ))}
                {reglasPuntos.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No hay reglas definidas. Creá una para empezar a fidelizar socios.</p>
                )}
            </div>

            {isModalOpen && (
                <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={selectedRegla ? 'Editar Regla' : 'Nueva Regla'}>
                    <ReglaForm regla={selectedRegla || {}} onSave={handleSaveRegla} onCancel={handleCloseModal} />
                </Modal>
            )}

            {/* Confirmation Modal */}
            {reglaToDelete && (
                <Modal isOpen={!!reglaToDelete} onClose={() => setReglaToDelete(null)} title="Confirmar Eliminación">
                     <div className="space-y-4">
                        <p className="text-gray-700">
                            ¿Estás seguro de que querés eliminar la regla: <span className="font-bold">{reglaToDelete.descripcion}</span>?
                        </p>
                        <div className="flex justify-end gap-3 pt-2">
                            <button 
                                onClick={() => setReglaToDelete(null)} 
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium"
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={confirmDelete} 
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium"
                            >
                                Eliminar
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ConfiguracionScreen;
